import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from "react";
import { Icon } from "../../../common/Icon";
import { buildD, parsePathData, parseSvgPath, dist } from "./svg-path-import.mjs";

/* ------------------------------------------------------------------ */
/*  Geometry helpers                                                    */
/*                                                                     */
/*  The vertex model (points with a corner radius), its serialisation  */
/*  (buildD) and the SVG path parser that maps ANY path back onto it   */
/*  (parseSvgPath) live in svg-path-import.mjs so they can be shared   */
/*  with the paste/drop import and unit-tested in node.                */
/* ------------------------------------------------------------------ */

function closestPointOnSegment(p, a, b) {
  const dx = b.x - a.x, dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return { point: { ...a }, t: 0, dist: dist(p, a) };
  let t = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const proj = { x: a.x + t * dx, y: a.y + t * dy };
  return { point: proj, t, dist: dist(p, proj) };
}

/* ------------------------------------------------------------------ */
/*  Styles                                                             */
/* ------------------------------------------------------------------ */

const COLORS = {
  bg: "#f0f0f077",
  accent: "#3d5afe",
  accentTransparent: "#3d5afe99",
  accentLight: "rgba(61,90,254,0.08)",
  stroke: "#1a1a2e",
  snap: "#ff1744",
  white: "#fff",
  whiteTransparent: "#fff9",
  muted: "#78909c",
  border: "#dde1e6",
  danger: "#ff1744",
};

const styles = {
  root: {
    width: "100%",
    height: "100%",
    background: COLORS.bg,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    userSelect: "none",
    position: "relative",
  },
  toolbar: {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    background: COLORS.white,
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    padding: 2,
    display: "flex",
    gap: 2,
    zIndex: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  toolBtn: (active) => ({
    padding: "8px 10px",
    borderRadius: 8,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: active ? COLORS.accentLight : "transparent",
    color: active ? COLORS.accent : COLORS.muted,
  }),
  panel: {
    position: "absolute",
    top: 16,
    right: 16,
    background: COLORS.white,
    padding: 8,
    borderRadius: 12,
    border: `1px solid ${COLORS.border}`,
    width: 140,
    zIndex: 10,
    boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
  },
  panelHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 6,
  },
  panelTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: "#333",
    margin: 0,
  },
  deleteBtn: {
    background: "none",
    border: "none",
    cursor: "pointer",
    color: COLORS.muted,
    padding: 4,
    borderRadius: 4,
    display: "flex",
  },
  label: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: 11,
    color: COLORS.muted,
    marginBottom: 4,
  },
  slider: {
    width: "100%",
    accentColor: COLORS.accent,
  },
  hint: {
    position: "absolute",
    bottom: 16,
    left: 16,
    background: "rgba(255,255,255,0.5)",
    padding: "5px 5px",
    borderRadius: 10,
    border: `1px solid ${COLORS.border}`,
    fontSize: 11,
    color: COLORS.muted,
    lineHeight: 1.7,
    zIndex: 10,
    pointerEvents: "none",    
  },
  hintKey: {
    color: "#333",
    fontWeight: 600,
  },
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export { buildD, parsePathData };

export default function FuseboxSVGPathEditor({ initialPaths, onDone, width = null, height = null, fuseLayout, imageAspectRatio, initialViewBoxHeight }) {
  const [paths, setPaths] = useState(() => {
    if (initialPaths && Array.isArray(initialPaths)) {
      // One editable path per subpath: an entry holding several subpaths (hand-pasted data) gets split,
      // and is stored back as separate entries by handleDone
      return initialPaths.flatMap((d) => {
        try {
          return parseSvgPath(d);
        } catch (err) {
          console.warn(`Ignoring unparseable svg path "${d}": ${err.message}`);
          return [];
        }
      });
    }
    return [];
  });
  const [currentPoints, setCurrentPoints] = useState([]);
  const [mode, setMode] = useState(initialPaths?.length ? "edit" : "draw");
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [draggingPoint, setDraggingPoint] = useState(null);
  const [dragStartPos, setDragStartPos] = useState(null);
  const [dragStartPaths, setDragStartPaths] = useState(null);
  const [dragOffset, setDragOffset] = useState(null);
  const [selectionRect, setSelectionRect] = useState(null);
  const [mousePos, setMousePos] = useState(null);
  const [snappedPos, setSnappedPos] = useState(null);
  const [segmentSnap, setSegmentSnap] = useState(null);
  const [snapLines, setSnapLines] = useState([]);
  const [shiftHeld, setShiftHeld] = useState(false);
  const svgRef = useRef(null);

  // Needed for keyboard shortcuts closure to read paths
  const pathsRef = useRef(paths);
  useEffect(() => { pathsRef.current = paths; }, [paths]);
  const selectedPointsRef = useRef(selectedPoints);
  useEffect(() => { selectedPointsRef.current = selectedPoints; }, [selectedPoints]);

  // On mount, scale paths from normalised space to editor pixel space.
  // When fuseLayout is provided, paths are in fuse-local coordinates and the
  // editor spans the entire image — map them to full-image pixel space.
  useLayoutEffect(() => {
    if (!svgRef.current) return;
    const { width: svgW, height: svgH } = svgRef.current.getBoundingClientRect();
    if (svgW <= 0 || svgH <= 0) return;

    if (fuseLayout && fuseLayout.width) {
      const vbH = initialViewBoxHeight || 100;
      const fusePixelLeft = (fuseLayout.left / 100) * svgW;
      const fusePixelTop = (fuseLayout.top / 100) * svgH;
      const fusePixelW = (fuseLayout.width / 100) * svgW;
      const fusePixelH = (fuseLayout.height / 100) * svgH;
      const sx = fusePixelW / 100;
      const sy = fusePixelH / vbH;
      setPaths(prev => prev.map(path => ({
        ...path,
        points: path.points.map(pt => ({
          x: fusePixelLeft + pt.x * sx,
          y: fusePixelTop + pt.y * sy,
          radius: pt.radius * (sx + sy) / 2,
        })),
      })));
    } else {
      const sx = svgW / 100, sy = svgH / 100;
      setPaths(prev => prev.map(path => ({
        ...path,
        points: path.points.map(pt => ({
          x: pt.x * sx,
          y: pt.y * sy,
          radius: pt.radius * (sx + sy) / 2,
        })),
      })));
    }
  }, []);

  /* ---------- Derived data ---------- */
  const selectedSet = useMemo(
    () => new Set(selectedPoints.map((s) => `${s.pi}-${s.pti}`)),
    [selectedPoints]
  );

  const displayPaths = useMemo(() => {
    if (mode === "edit" && draggingPoint && dragOffset) {
      return paths.map((path, pi) => ({
        ...path,
        points: path.points.map((pt, pti) => {
          if (
            selectedSet.has(`${pi}-${pti}`) ||
            (draggingPoint.pi === pi && draggingPoint.pti === pti)
          ) {
            return { ...pt, x: pt.x + dragOffset.x, y: pt.y + dragOffset.y };
          }
          return pt;
        }),
      }));
    }
    return paths;
  }, [paths, mode, draggingPoint, dragOffset, selectedSet]);

  /* ---------- Keyboard ---------- */
  useEffect(() => {
    const down = (e) => {
      if (e.target instanceof HTMLInputElement) return;
      if (e.key === "Shift") setShiftHeld(true);
      // Alt is a snapping modifier here; stop the browser from moving focus to its menu bar
      if (e.key === "Alt") e.preventDefault();
      
      if ((e.key === "v" || e.key === "V") && !e.ctrlKey) {
        setMode("edit");
        setCurrentPoints([]);
      }

      if (e.key === "p" || e.key === "P") {
        setMode("draw");
        setSelectedPoints([]);
      }

      if(e.key === "a" && e.ctrlKey) {
        setMode("edit");
        setCurrentPoints([]);
        const allPaths = pathsRef.current;
        const selectAll = () => allPaths.flatMap((p, pi) => p.points.map((_pt, pti) => ({ pi, pti })));

        setSelectedPoints((prevSel) => {
          if (prevSel.length === 0) return selectAll();
          const affectedPis = new Set(prevSel.map((s) => s.pi));
          const allAffectedFullySelected = [...affectedPis].every((pi) => {
            const pathLen = allPaths[pi]?.points.length ?? 0;
            const selectedCount = prevSel.filter((s) => s.pi === pi).length;
            return selectedCount >= pathLen;
          });
          if (allAffectedFullySelected) return selectAll();
          const expanded = [...prevSel];
          const existingSet = new Set(prevSel.map((s) => `${s.pi}-${s.pti}`));
          for (const pi of affectedPis) {
            const pathLen = allPaths[pi]?.points.length ?? 0;
            for (let pti = 0; pti < pathLen; pti++) {
              if (!existingSet.has(`${pi}-${pti}`)) expanded.push({ pi, pti });
            }
          }
          return expanded;
        });
        e.preventDefault();
        e.stopPropagation();
      }

      if (e.key === "c" && e.ctrlKey) {
        const sel = selectedPointsRef.current;
        if (!sel.length) return;
        const cur = pathsRef.current;
        const pis = [...new Set(sel.map((s) => s.pi))].sort((a, b) => a - b);
        const copied = pis.map((pi) => cur[pi]).filter(Boolean);
        if (copied.length) navigator.clipboard.writeText(JSON.stringify(copied));
        e.preventDefault();
      }

      if (e.key === "v" && e.ctrlKey) {
        navigator.clipboard.readText().then((text) => {
          try {
            const parsed = JSON.parse(text);
            if (!Array.isArray(parsed) || !parsed.length) return;
            const OFFSET = 10;
            const shifted = parsed.map((p) => ({
              ...p,
              points: p.points.map((pt) => ({ ...pt, x: pt.x + OFFSET, y: pt.y + OFFSET })),
            }));
            setPaths((prev) => {
              const base = prev.length;
              setSelectedPoints(
                shifted.flatMap((p, i) => p.points.map((_pt, pti) => ({ pi: base + i, pti })))
              );
              return [...prev, ...shifted];
            });
            setMode("edit");
          } catch { /* not our data */ }
        });
        e.preventDefault();
      }

      if (e.key === "Escape" || e.key === "Enter") {
        setCurrentPoints((prev) => {
          if (prev.length > 0) {
            setPaths((p) => [...p, { points: prev, isClosed: false }]);
            return [];
          }
          return prev;
        });
        setSelectedPoints([]);
        e.preventDefault();
        e.stopPropagation();
      }

      if(e.key === "Enter") {
        handleDone();
      }

      if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowRight") {
        const sel = selectedPointsRef.current;
        if (!sel.length) return;
        const delta = e.shiftKey ? 10 : 1;
        const dx = e.key === "ArrowLeft" ? -delta : e.key === "ArrowRight" ? delta : 0;
        const dy = e.key === "ArrowUp" ? -delta : e.key === "ArrowDown" ? delta : 0;
        setPaths(prev => {
          const selSet = new Set(sel.map(s => `${s.pi}-${s.pti}`));
          return prev.map((path, pi) => ({
            ...path,
            points: path.points.map((pt, pti) =>
              selSet.has(`${pi}-${pti}`) ? { ...pt, x: pt.x + dx, y: pt.y + dy } : pt
            ),
          }));
        });
        e.preventDefault();
      }

      if (e.key === "Backspace" || e.key === "Delete") {
        setSelectedPoints((prevSel) => {
          if (prevSel.length > 0) {
            setPaths((prevPaths) => {
              const np = prevPaths.map((p) => ({ ...p, points: [...p.points] }));
              [...prevSel]
                .sort((a, b) => (a.pi !== b.pi ? b.pi - a.pi : b.pti - a.pti))
                .forEach((s) => np[s.pi].points.splice(s.pti, 1));
              return np.filter((p) => p.points.length > 0);
            });
            return [];
          }
          return prevSel;
        });
      }
    };

    const up = (e) => {
      if (e.key === "Shift") setShiftHeld(false);
    };
    
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  /* ---------- Snapping ---------- */
  // Modifiers: Shift = free angle (still snaps to points) · Alt = no point snapping (keeps the
  // horizontal/vertical lock) · Ctrl = raw mouse, nothing at all
  const snap = useCallback(
    (rawX, rawY, isDrag, skipPointSnap = false) => {
      let sx = rawX,
        sy = rawY;
      const lines = [];
      const THRESH = 10;

      const allPts = paths
        .flatMap((p, pi) => p.points.map((pt, pti) => ({ ...pt, pi, pti })))
        .concat(currentPoints.map((pt, pti) => ({ ...pt, pi: -1, pti })));

      let lockX = false,
        lockY = false;

      // Axis constraint while drawing
      if (mode === "draw" && currentPoints.length > 0 && !shiftHeld) {
        const prev = currentPoints[currentPoints.length - 1];
        if (Math.abs(rawX - prev.x) > Math.abs(rawY - prev.y)) {
          sy = prev.y;
          lockY = true;
          lines.push({ t: "y", v: prev.y });
        } else {
          sx = prev.x;
          lockX = true;
          lines.push({ t: "x", v: prev.x });
        }
      }

      if (skipPointSnap) return { sx, sy, lines };

      // Point snapping
      const targets = isDrag
        ? allPts.filter(
            (p) =>
              !selectedSet.has(`${p.pi}-${p.pti}`) &&
              !(draggingPoint && draggingPoint.pi === p.pi && draggingPoint.pti === p.pti)
          )
        : allPts;

      let snapXDone = false,
        snapYDone = false;
      for (const p of targets) {
        if (!lockX && !snapXDone && Math.abs(rawX - p.x) < THRESH) {
          sx = p.x;
          snapXDone = true;
          lines.push({ t: "x", v: p.x });
        }
        if (!lockY && !snapYDone && Math.abs(rawY - p.y) < THRESH) {
          sy = p.y;
          snapYDone = true;
          lines.push({ t: "y", v: p.y });
        }
      }
      return { sx, sy, lines };
    },
    [paths, currentPoints, mode, shiftHeld, selectedSet, draggingPoint]
  );

  /* ---------- Mouse handlers ---------- */
  const onMove = useCallback(
    (e) => {
      const r = e.currentTarget.getBoundingClientRect();
      const rx = e.clientX - r.left;
      const ry = e.clientY - r.top;
      setMousePos({ x: rx, y: ry });

      if (selectionRect) {
        setSelectionRect((sr) => (sr ? { ...sr, endX: rx, endY: ry } : sr));
        return;
      }

      const { sx, sy, lines } = snap(rx, ry, !!draggingPoint, e.altKey);

      let segSnap = null;
      if (mode === "draw" && !e.ctrlKey && !e.altKey) {
        const SEG_THRESH = 10;
        const nearVertex = paths.some(p => p.points.some(pt => dist(pt, { x: rx, y: ry }) < SEG_THRESH));
        if (!nearVertex) {
          let bestDist = SEG_THRESH;
          for (let pi = 0; pi < paths.length; pi++) {
            const pts = paths[pi].points;
            const segCount = paths[pi].isClosed ? pts.length : pts.length - 1;
            for (let si = 0; si < segCount; si++) {
              const a = pts[si];
              const b = pts[(si + 1) % pts.length];
              const res = closestPointOnSegment({ x: rx, y: ry }, a, b);
              if (res.dist < bestDist && res.t > 0.05 && res.t < 0.95) {
                bestDist = res.dist;
                segSnap = { pi, segIdx: si, point: res.point };
              }
            }
          }
        }
      }
      setSegmentSnap(segSnap);

      if(!e.ctrlKey) {
        if (segSnap) {
          setSnappedPos(segSnap.point);
          setSnapLines([]);
        } else {
          setSnappedPos({ x: sx, y: sy });
          setSnapLines(lines);
        }
      } else {
        setSnappedPos({x: rx, y: ry});
        setSnapLines([]);
      }

      if (mode === "edit" && draggingPoint && dragStartPos) {
        const ox = e.ctrlKey ? rx : sx;
        const oy = e.ctrlKey ? ry : sy;
        setDragOffset({ x: ox - dragStartPos.x, y: oy - dragStartPos.y });
      }
    },
    [selectionRect, snap, mode, draggingPoint, dragStartPos, paths]
  );

  const onDown = useCallback(
    (e) => {
      if (!snappedPos || !mousePos) return;
      
      if (mode === "draw") {
        if (segmentSnap && currentPoints.length === 0) {
          setPaths(prev => {
            const np = prev.map((p, i) => i === segmentSnap.pi ? { ...p, points: [...p.points] } : p);
            np[segmentSnap.pi].points.splice(segmentSnap.segIdx + 1, 0, {
              x: segmentSnap.point.x,
              y: segmentSnap.point.y,
              radius: 0,
            });
            return np;
          });
          return;
        }
        if (currentPoints.length > 2) {
          const first = currentPoints[0];
          if (Math.abs(snappedPos.x - first.x) < 10 && Math.abs(snappedPos.y - first.y) < 10) {
            setPaths((p) => [...p, { points: currentPoints, isClosed: true }]);
            setCurrentPoints([]);
            return;
          }
        }
        setCurrentPoints((c) => [...c, { x: snappedPos.x, y: snappedPos.y, radius: 0 }]);
      } else {
        // edit mode – find clicked point
        let hit = null;
        for (let i = paths.length - 1; i >= 0 && !hit; i--) {
          for (let j = 0; j < paths[i].points.length; j++) {
            const p = paths[i].points[j];
            if (Math.abs(snappedPos.x - p.x) < 10 && Math.abs(snappedPos.y - p.y) < 10) {
              hit = { pi: i, pti: j };
              break;
            }
          }
        }

        if (hit) {
          const alreadySel = selectedPoints.some((s) => s.pi === hit.pi && s.pti === hit.pti);
          if (shiftHeld) {
            setSelectedPoints((sp) =>
              alreadySel ? sp.filter((s) => !(s.pi === hit.pi && s.pti === hit.pti)) : [...sp, hit]
            );
          } else if (!alreadySel) {
            setSelectedPoints([hit]);
          }
          setDraggingPoint(hit);
          setDragStartPos({ x: snappedPos.x, y: snappedPos.y });
          setDragStartPaths(paths);
          e.currentTarget.setPointerCapture(e.pointerId);
        } else {
          if (!shiftHeld) setSelectedPoints([]);
          setSelectionRect({ startX: mousePos.x, startY: mousePos.y, endX: mousePos.x, endY: mousePos.y });
          e.currentTarget.setPointerCapture(e.pointerId);
        }
      }
    },
    [snappedPos, mousePos, mode, currentPoints, paths, selectedPoints, shiftHeld, segmentSnap]
  );

  const onUp = useCallback(() => {
    if (selectionRect) {
      const minX = Math.min(selectionRect.startX, selectionRect.endX);
      const maxX = Math.max(selectionRect.startX, selectionRect.endX);
      const minY = Math.min(selectionRect.startY, selectionRect.endY);
      const maxY = Math.max(selectionRect.startY, selectionRect.endY);

      const inRect = [];
      paths.forEach((path, pi) => {
        path.points.forEach((pt, pti) => {
          if (pt.x >= minX && pt.x <= maxX && pt.y >= minY && pt.y <= maxY) {
            inRect.push({ pi, pti });
          }
        });
      });

      if (shiftHeld) {
        setSelectedPoints((prev) => {
          const next = [...prev];
          inRect.forEach((r) => {
            const idx = next.findIndex((s) => s.pi === r.pi && s.pti === r.pti);
            if (idx >= 0) next.splice(idx, 1);
            else next.push(r);
          });
          return next;
        });
      } else {
        setSelectedPoints(inRect);
      }
      setSelectionRect(null);
    }

    if (draggingPoint && dragOffset && dragStartPaths) {
      setPaths(
        dragStartPaths.map((path, pi) => ({
          ...path,
          points: path.points.map((pt, pti) => {
            if (
              selectedSet.has(`${pi}-${pti}`) ||
              (draggingPoint.pi === pi && draggingPoint.pti === pti)
            ) {
              return { ...pt, x: pt.x + dragOffset.x, y: pt.y + dragOffset.y };
            }
            return pt;
          }),
        }))
      );
    }

    setDraggingPoint(null);
    setDragStartPos(null);
    setDragStartPaths(null);
    setDragOffset(null);
  }, [selectionRect, draggingPoint, dragOffset, dragStartPaths, paths, shiftHeld, selectedSet]);

  /* ---------- Render helpers ---------- */
  const radiusValue = useMemo(() => {
    if (!selectedPoints.length) return 0;
    const first = paths[selectedPoints[0].pi]?.points[selectedPoints[0].pti]?.radius;
    const allSame = selectedPoints.every(
      (s) => paths[s.pi]?.points[s.pti]?.radius === first
    );
    return { value: allSame ? first || 0 : 0, mixed: !allSame };
  }, [selectedPoints, paths]);

  const setRadius = useCallback(
    (r) => {
      setPaths((prev) => {
        const np = prev.map((p) => ({ ...p, points: [...p.points] }));
        selectedPoints.forEach((s) => {
          if (np[s.pi]?.points[s.pti]) np[s.pi].points[s.pti].radius = r;
        });
        return np;
      });
    },
    [selectedPoints]
  );

  const deleteSelected = useCallback(() => {
    setPaths((prev) => {
      const np = prev.map((p) => ({ ...p, points: [...p.points] }));
      [...selectedPoints]
        .sort((a, b) => (a.pi !== b.pi ? b.pi - a.pi : b.pti - a.pti))
        .forEach((s) => np[s.pi].points.splice(s.pti, 1));
      return np.filter((p) => p.points.length > 0);
    });
    setSelectedPoints([]);
  }, [selectedPoints]);

  const handleDone = useCallback(() => {
    let finalPaths = paths;
    if (currentPoints.length > 0) {
      finalPaths = [...paths, { points: currentPoints, isClosed: false }];
    }

    const allPoints = finalPaths.flatMap(p => p.points);
    if (!allPoints.length) {
      if (onDone) onDone([], fuseLayout || null, 100);
      return;
    }

    const el = svgRef.current;
    if (!el) return;
    const { width: svgW, height: svgH } = el.getBoundingClientRect();

    if (fuseLayout) {
      // Crop to the bounding box of all paths (in pixel space)
      const PADDING = 0;
      let minX = Math.min(...allPoints.map(p => p.x)) - PADDING;
      let minY = Math.min(...allPoints.map(p => p.y)) - PADDING;
      let maxX = Math.max(...allPoints.map(p => p.x)) + PADDING;
      let maxY = Math.max(...allPoints.map(p => p.y)) + PADDING;      

      const cropW = maxX - minX;
      const cropH = maxY - minY;
      if (cropW <= 0 || cropH <= 0) {
        if (onDone) onDone([], fuseLayout, 100);
        return;
      }

      const newLayout = {
        left: (minX / svgW) * 100,
        top: (minY / svgH) * 100,
        width: (cropW / svgW) * 100,
        height: (cropH / svgH) * 100,
      };

      // Uniform scale: 100 units = crop width; Y uses the same scale so
      // strokes are never deformed regardless of aspect ratio.
      const scale = 100 / cropW;
      const viewBoxHeight = cropH * scale;

      finalPaths = finalPaths.map(p => ({
        ...p,
        points: p.points.map(pt => ({
          x: (pt.x - minX) * scale,
          y: (pt.y - minY) * scale,
          radius: pt.radius * scale,
        })),
      }));

      const dStrings = finalPaths.map(p => buildD(p.points, p.isClosed)).filter(Boolean);
      if (onDone) onDone(dStrings, newLayout, viewBoxHeight);
    } else {
      // Legacy fallback: scale back to [0,100] normalised space
      const sx = svgW / 100, sy = svgH / 100;
      finalPaths = finalPaths.map(p => ({
        ...p,
        points: p.points.map(pt => ({
          x: pt.x / sx,
          y: pt.y / sy,
          radius: pt.radius / ((sx + sy) / 2),
        })),
      }));
      const dStrings = finalPaths.map(p => buildD(p.points, p.isClosed)).filter(Boolean);
      if (onDone) onDone(dStrings);
    }
  }, [paths, currentPoints, onDone, fuseLayout]);

  /* ---------- JSX ---------- */
  const rootStyle = { ...styles.root };
  if (width) rootStyle.width = typeof width === "number" ? `${width}px` : width;
  if (height) rootStyle.height = typeof height === "number" ? `${height}px` : height;

  return (
    <div style={rootStyle}>
      {/* Toolbar */}
      <div style={styles.toolbar}>
        <button
          style={styles.toolBtn(mode === "edit")}
          onClick={() => { setMode("edit"); setCurrentPoints([]); }}
          title="Edit Mode (V)"
        >
          <Icon icon="near_me" size="sm" />
        </button>
        <button
          style={styles.toolBtn(mode === "draw")}
          onClick={() => { setMode("draw"); setSelectedPoints([]); }}
          title="Pen Tool (P)"
        >
          <Icon icon="edit" size="sm" />
        </button>
      </div>

      {/* Properties panel */}
      {selectedPoints.length > 0 && mode === "edit" && (
        <div style={styles.panel}>
          <div style={styles.panelHeader}>
            <h6 style={styles.panelTitle}>
              {selectedPoints.length === 1
                ? "Point"
                : `${selectedPoints.length} Points`}
            </h6>
            <button style={styles.deleteBtn} onClick={deleteSelected} title="Delete (Backspace)">
              <Icon icon="delete" size="sm" />
            </button>
          </div>
          <div style={styles.label}>
            <span>Corner Radius</span>
            <span style={{ fontFamily: "monospace" }}>
              {radiusValue.mixed ? "Mixed" : `${radiusValue.value?.toFixed(1)}px`}
            </span>
          </div>
          <input
            type="range"
            min="0"
            max="400"
            style={styles.slider}
            value={radiusValue.value}
            onChange={(e) => setRadius(parseInt(e.target.value))}
          />
        </div>
      )}

      {/* Hints */}
      <div style={styles.hint}>
        <span style={styles.hintKey}>Click</span> add point &nbsp;·&nbsp;
        <span style={styles.hintKey}>Near segment</span> insert vertex &nbsp;·&nbsp;
        <span style={styles.hintKey}>Shift</span> free angle &nbsp;·&nbsp;
        <span style={styles.hintKey}>Alt</span> no snap &nbsp;·&nbsp;
        <span style={styles.hintKey}>Ctrl</span> free &nbsp;·&nbsp;
        <span style={styles.hintKey}>Enter</span> finish &nbsp;·&nbsp;
        <span style={styles.hintKey}>⌫</span> delete
      </div>

      {/* Done button */}
      {onDone && (
        <button
          onClick={handleDone}
          style={{
            position: "absolute", bottom: 2, right: 2, zIndex: 10,
            padding: "4px 8px", borderRadius: 8, border: "none",
            background: COLORS.accent, color: COLORS.white,
            fontWeight: 600, cursor: "pointer",
            zoom: 0.75,
            boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
          }}
        >
          Done
        </button>
      )}

      {/* Canvas */}
      <svg
        ref={svgRef}
        style={{ width: "100%", height: "100%", cursor: "crosshair", touchAction: "none" }}
        onPointerMove={onMove}
        onPointerDown={onDown}
        onPointerUp={onUp}
      >
        {/* Selection rectangle */}
        {selectionRect && (
          <rect
            x={Math.min(selectionRect.startX, selectionRect.endX)}
            y={Math.min(selectionRect.startY, selectionRect.endY)}
            width={Math.abs(selectionRect.endX - selectionRect.startX)}
            height={Math.abs(selectionRect.endY - selectionRect.startY)}
            fill="rgba(61,90,254,0.08)"
            stroke={COLORS.accent}
            strokeWidth="1"
          />
        )}

        {/* Snap guides */}
        {snapLines.map((l, i) =>
          l.t === "x" ? (
            <line key={`sx${i}`} x1={l.v} y1={0} x2={l.v} y2="100%" stroke={COLORS.snap} strokeWidth="1" strokeDasharray="4 4" />
          ) : (
            <line key={`sy${i}`} x1={0} y1={l.v} x2="100%" y2={l.v} stroke={COLORS.snap} strokeWidth="1" strokeDasharray="4 4" />
          )
        )}

        {/* Compound fill preview (shows holes via evenodd) */}
        {displayPaths.length > 0 && (
          <path
            d={displayPaths.map(p => buildD(p.points, p.isClosed)).join(' ')}
            fill="rgba(26,26,46,0.12)"
            fillRule="evenodd"
            stroke="none"
          />
        )}

        {/* Committed paths */}
        {displayPaths.map((path, pi) => (
          <g key={pi}>
            <path
              d={buildD(path.points, path.isClosed)}
              fill="none"
              stroke={COLORS.stroke}
              strokeWidth="2"
              strokeLinejoin="round"
              strokeLinecap="round"
            />
            {mode === "edit" &&
              path.points.map((p, pti) => {
                const sel = selectedSet.has(`${pi}-${pti}`);
                return (
                  <circle
                    key={pti}
                    cx={p.x}
                    cy={p.y}
                    r={sel ? 6 : 4}
                    fill={sel ? COLORS.accentTransparent : COLORS.whiteTransparent}
                    stroke={sel ? COLORS.white : COLORS.accent}
                    strokeWidth="2"
                    style={{ cursor: "pointer" }}
                  />
                );
              })}
          </g>
        ))}

        {/* Segment snap preview */}
        {mode === "draw" && segmentSnap && (() => {
          const pts = paths[segmentSnap.pi]?.points;
          if (!pts) return null;
          const a = pts[segmentSnap.segIdx];
          const b = pts[(segmentSnap.segIdx + 1) % pts.length];
          return (
            <g>
              <line
                x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                stroke={COLORS.snap}
                strokeWidth="3"
                opacity="0.5"
              />
              <circle
                cx={segmentSnap.point.x} cy={segmentSnap.point.y}
                r="5"
                fill={COLORS.snap}
                stroke={COLORS.white}
                strokeWidth="2"
              />
            </g>
          );
        })()}

        {/* Live preview while drawing */}
        {mode === "draw" && currentPoints.length > 0 && snappedPos && (
          <g>
            <path
              d={buildD([...currentPoints, { x: snappedPos.x, y: snappedPos.y, radius: 0 }], false)}
              fill="none"
              stroke={COLORS.accent}
              strokeWidth="2"
              strokeDasharray="4 4"
            />
            {currentPoints.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r="4" fill={COLORS.white} stroke={COLORS.accent} strokeWidth="2" />
            ))}
            {/* Close-path indicator */}
            {currentPoints.length > 2 &&
              Math.abs(snappedPos.x - currentPoints[0].x) < 10 &&
              Math.abs(snappedPos.y - currentPoints[0].y) < 10 && (
                <circle cx={currentPoints[0].x} cy={currentPoints[0].y} r="8" fill="none" stroke={COLORS.snap} strokeWidth="2" />
              )}
            <circle cx={snappedPos.x} cy={snappedPos.y} r="4" fill={COLORS.accent} />
          </g>
        )}
      </svg>
    </div>
  );
}